import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { MediaItem, Filters, SortOption, MediaType } from '../types/movie';
import { discoverMedia, searchMedia, getMediaDetails, type MediaDiscoverResponse } from '../api/tmdb';
import { DEFAULT_FILTERS } from '../utils/constants';
import { debounce, calculateOurRating } from '../utils/helpers';

// Cache for media details (runtime, etc.) to avoid refetching
const mediaDetailsCache = new Map<string, { runtime?: number }>();

interface UseMoviesResult {
  movies: MediaItem[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  totalResults: number;
  loadMore: () => void;
  filters: Filters;
  setFilters: (filters: Filters) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

// Sort options that require client-side sorting
const CLIENT_SORT_OPTIONS: SortOption[] = [
  'our_rating.desc', 'our_rating.asc',
  'imdb_rating.desc', 'imdb_rating.asc',
  'rt_rating.desc', 'rt_rating.asc',
  'runtime.desc', 'runtime.asc',
];

// Sort media items client-side
function sortMedia(items: MediaItem[], sortBy: SortOption): MediaItem[] {
  const [field, order] = sortBy.split('.') as [string, 'asc' | 'desc'];

  return [...items].sort((a, b) => {
    let aVal: number = 0;
    let bVal: number = 0;

    switch (field) {
      case 'our_rating':
        aVal = calculateOurRating(a.imdb_rating, a.rt_rating) || 0;
        bVal = calculateOurRating(b.imdb_rating, b.rt_rating) || 0;
        break;
      case 'imdb_rating':
        aVal = a.imdb_rating || 0;
        bVal = b.imdb_rating || 0;
        break;
      case 'rt_rating':
        aVal = a.rt_rating || 0;
        bVal = b.rt_rating || 0;
        break;
      case 'runtime':
        aVal = a.runtime || 0;
        bVal = b.runtime || 0;
        break;
      case 'popularity':
        aVal = a.popularity || 0;
        bVal = b.popularity || 0;
        break;
      case 'release_date':
        aVal = a.release_date ? new Date(a.release_date).getTime() : 0;
        bVal = b.release_date ? new Date(b.release_date).getTime() : 0;
        break;
      case 'vote_average':
        aVal = a.vote_average || 0;
        bVal = b.vote_average || 0;
        break;
      default:
        return 0;
    }

    if (order === 'desc') {
      return bVal - aVal;
    }
    return aVal - bVal;
  });
}

export function useMovies(initialFilters?: Partial<Filters>, mediaType: MediaType = 'movie'): UseMoviesResult {
  const [rawMovies, setRawMovies] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [filters, setFilters] = useState<Filters>({
    ...(DEFAULT_FILTERS as Filters),
    ...initialFilters,
  });
  const [searchQuery, setSearchQuery] = useState('');

  const abortControllerRef = useRef<AbortController | null>(null);
  const detailsFetchingRef = useRef<Set<string>>(new Set());

  // Fetch runtime details for media that don't have it
  useEffect(() => {
    const fetchMissingDetails = async () => {
      const itemsToFetch = rawMovies.filter(item => {
        const cacheKey = `${item.media_type || mediaType}:${item.id}`;
        return !item.runtime && !mediaDetailsCache.has(cacheKey) && !detailsFetchingRef.current.has(cacheKey);
      });

      if (itemsToFetch.length === 0) return;

      // Mark as fetching to prevent duplicate requests
      itemsToFetch.forEach(item => {
        const cacheKey = `${item.media_type || mediaType}:${item.id}`;
        detailsFetchingRef.current.add(cacheKey);
      });

      // Fetch details in batches of 5 to avoid overwhelming the API
      const batchSize = 5;
      for (let i = 0; i < itemsToFetch.length; i += batchSize) {
        const batch = itemsToFetch.slice(i, i + batchSize);

        const details = await Promise.all(
          batch.map(async item => {
            try {
              const itemType = item.media_type || mediaType;
              const details = await getMediaDetails(item.id, itemType);
              return { id: item.id, type: itemType, runtime: details.runtime };
            } catch {
              return { id: item.id, type: item.media_type || mediaType, runtime: undefined };
            }
          })
        );

        // Update cache
        details.forEach(d => {
          const cacheKey = `${d.type}:${d.id}`;
          mediaDetailsCache.set(cacheKey, { runtime: d.runtime });
        });

        // Update media state with runtime
        setRawMovies(prev => prev.map(item => {
          const cacheKey = `${item.media_type || mediaType}:${item.id}`;
          const cached = mediaDetailsCache.get(cacheKey);
          if (cached?.runtime && !item.runtime) {
            return { ...item, runtime: cached.runtime };
          }
          return item;
        }));
      }
    };

    fetchMissingDetails();
  }, [rawMovies, mediaType]);

  // Apply client-side filtering and sorting when needed
  const movies = useMemo(() => {
    let filtered = rawMovies;

    // Client-side runtime filtering (fallback in case API doesn't filter properly)
    if (filters.runtimeRange[0] > 0 || filters.runtimeRange[1] < 300) {
      filtered = filtered.filter(item => {
        // If item has no runtime data, include it (we can't filter it)
        if (!item.runtime) return true;
        return item.runtime >= filters.runtimeRange[0] && item.runtime <= filters.runtimeRange[1];
      });
    }

    // Client-side sorting
    if (CLIENT_SORT_OPTIONS.includes(filters.sortBy)) {
      return sortMedia(filtered, filters.sortBy);
    }
    return filtered;
  }, [rawMovies, filters.sortBy, filters.runtimeRange]);

  // Fetch media from API
  const fetchMovies = useCallback(
    async (currentFilters: Filters, currentPage: number, append = false) => {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError(null);

      try {
        let response: MediaDiscoverResponse;

        // For client-sorted options, use popularity.desc from API
        const apiFilters = { ...currentFilters };
        if (CLIENT_SORT_OPTIONS.includes(currentFilters.sortBy)) {
          apiFilters.sortBy = 'popularity.desc';
        }

        if (searchQuery) {
          response = await searchMedia(searchQuery, currentPage, mediaType);
        } else {
          response = await discoverMedia(apiFilters, currentPage, mediaType);
        }

        if (append) {
          setRawMovies((prev) => [...prev, ...response.results]);
        } else {
          setRawMovies(response.results);
        }

        setTotalPages(response.total_pages);
        setTotalResults(response.total_results);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        console.error('Error fetching media:', err);
        setError('Failed to fetch content');
        setRawMovies([]);
        setTotalPages(0);
        setTotalResults(0);
      } finally {
        setLoading(false);
      }
    },
    [searchQuery, mediaType]
  );

  // Debounced fetch for filter changes
  const debouncedFetch = useCallback(
    debounce((currentFilters: Filters) => {
      setPage(1);
      fetchMovies(currentFilters, 1, false);
    }, 300),
    [fetchMovies]
  );

  // Effect for filter changes
  useEffect(() => {
    debouncedFetch(filters);
  }, [filters, debouncedFetch]);

  // Effect for search query changes
  useEffect(() => {
    setPage(1);
    fetchMovies(filters, 1, false);
  }, [searchQuery]);

  // Effect for media type changes - reset filters and refetch
  useEffect(() => {
    setPage(1);
    // Reset genre filters when switching media type as genres are different
    setFilters(prev => ({
      ...prev,
      genres: [],
    }));
    fetchMovies({ ...filters, genres: [] }, 1, false);
  }, [mediaType]);

  const loadMore = useCallback(() => {
    if (page < totalPages && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchMovies(filters, nextPage, true);
    }
  }, [page, totalPages, loading, filters, fetchMovies]);

  return {
    movies,
    loading,
    error,
    hasMore: page < totalPages,
    totalResults,
    loadMore,
    filters,
    setFilters,
    searchQuery,
    setSearchQuery,
  };
}
